import { useState, useEffect, useRef, useCallback } from "react";
import { buildPhotoUrl, isPhotoProxyUrl } from "@/lib/photoProxy";
import { getSignedUrl } from "@/lib/signedUrl";
import { ImageOff } from "lucide-react";
import { cn } from "@/lib/utils";
import { PhotoTile, PHOTO_TILE_FILL_STYLE } from "@/components/PhotoTile";
import { photoTileRenderingEnabled } from "@/lib/photoTileRuntime";

function extractPath(urlOrPath: string, bucket: string): string {
  if (!urlOrPath || !urlOrPath.startsWith("http")) return urlOrPath;
  const publicPattern = `/storage/v1/object/public/${bucket}/`;
  const signedPattern = `/storage/v1/object/sign/${bucket}/`;
  let idx = urlOrPath.indexOf(publicPattern);
  if (idx !== -1) return urlOrPath.substring(idx + publicPattern.length);
  idx = urlOrPath.indexOf(signedPattern);
  if (idx !== -1) return urlOrPath.substring(idx + signedPattern.length).split("?")[0];
  const bucketIdx = urlOrPath.indexOf(`/${bucket}/`);
  if (bucketIdx !== -1) return urlOrPath.substring(bucketIdx + bucket.length + 2);
  return urlOrPath;
}

/** Bounded signed-URL resolution attempts for a thumbnail (1.65). */
const SIGNED_URL_ATTEMPTS = 2;
const SIGNED_URL_RETRY_DELAY_MS = 150;


interface SignedPhotoProps {
  url: string;
  alt: string;
  className?: string;
  bucket?: string;
  onClick?: () => void;
  /**
   * Browser loading hint. Defaults to "lazy". Completed-job galleries pass
   * "eager": lazy loads inside the scrolling Photos dialog could stay
   * unstarted in the native WebView, leaving grey tiles (2026-10-21).
   */
  loading?: "lazy" | "eager";
  /**
   * Deprecated compatibility prop; private activation is application-wide.
   * This prop does not select jobs, companies or galleries.
   */
  privateTestSurface?: boolean;
  /** Fill a relative aspect-ratio parent (WebKit percentage-height fix); see PhotoTile. */
  fill?: boolean;
}

export const SignedPhoto = (props: SignedPhotoProps) =>
  <LegacySignedPhoto {...props} />;

const LegacySignedPhoto = ({ url, alt, className, bucket = "appraisal-photos", onClick, loading = "lazy", fill = false }: SignedPhotoProps) => {
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(() => {
    if (!url) return null;
    if (isPhotoProxyUrl(url) || (url.includes("/object/sign/") && url.includes("token="))) return url;
    const path = extractPath(url, bucket);
    return path.startsWith("http") || /^(blob:|data:|capacitor:|file:)/.test(path) ? path : buildPhotoUrl(bucket, path);
  });
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  // Preview-only retry counter; independent of the photo's upload lifecycle.
  const [attempt, setAttempt] = useState(0);
  const imgRef = useRef<HTMLImageElement | null>(null);
  // A single signed-URL retry per source; prevents loops on genuinely missing objects.
  const retriedRef = useRef<string | null>(null);

  useEffect(() => {
    setLoaded(false);
    setFailed(false);
    retriedRef.current = null;

    if (!url || typeof url !== "string" || url.length === 0) {
      setResolvedUrl(null);
      setFailed(true);
      return;
    }
    if (isPhotoProxyUrl(url) || (url.includes("/object/sign/") && url.includes("token="))) {
      setResolvedUrl(url);
      return;
    }
    const path = extractPath(url, bucket);
    if (!path || path.length === 0) {
      setResolvedUrl(null);
      setFailed(true);
      return;
    }
    setResolvedUrl(path.startsWith("http") || /^(blob:|data:|capacitor:|file:)/.test(path) ? path : buildPhotoUrl(bucket, path));
  }, [url, bucket, attempt]);

  // A cached image can finish loading before React attaches onLoad, which used to
  // leave the thumbnail stuck at opacity-0 (blank grey tile). Check directly.
  const attachRef = useCallback((node: HTMLImageElement | null) => {
    imgRef.current = node;
    if (node?.complete && node.naturalWidth > 0) setLoaded(true);
  }, []);

  useEffect(() => {
    const node = imgRef.current;
    if (node?.complete && node.naturalWidth > 0) setLoaded(true);
  }, [resolvedUrl]);

  const handleError = async () => {
    const current = resolvedUrl;
    if (current && retriedRef.current !== current && !current.startsWith("blob:") && !current.startsWith("data:")) {
      retriedRef.current = current;
      const path = extractPath(current, bucket);
      // 1.65 (J-1074) — a single failed signing attempt is NOT evidence that
      // the photo is missing: the full-size viewer resolves the same object
      // fine moments later. Signing is retried a bounded number of times
      // before the placeholder is shown. Nothing about the stored photo or its
      // reference is changed by a preview failure.
      for (let attemptIndex = 0; attemptIndex < SIGNED_URL_ATTEMPTS; attemptIndex++) {
        if (attemptIndex > 0) {
          await new Promise((resolve) => setTimeout(resolve, SIGNED_URL_RETRY_DELAY_MS));
        }
        const signed = await getSignedUrl(bucket, path).catch(() => null);
        if (signed) {
          setResolvedUrl(signed);
          return;
        }
      }
    }
    setFailed(true);
  };


  if (failed || !resolvedUrl) {
    if (!url && photoTileRenderingEnabled()) return <PhotoTile source="" alt={alt} className={className} bucket={bucket} fill={fill} />;
    // A preview failure says nothing about whether the photo exists — it is a
    // thumbnail problem only, and retryable on its own. Never phrase it as a
    // missing photo, and never let it change the photo's upload state.
    return (
      <div
        className={cn("flex flex-col items-center justify-center gap-1 bg-muted text-muted-foreground", className)}
        style={fill ? PHOTO_TILE_FILL_STYLE : undefined}
        role="img"
        aria-label={`${alt} (preview unavailable)`}
        title="Preview unavailable — tap to retry"
        onClick={() => { retriedRef.current = null; setFailed(false); setAttempt((n) => n + 1); }}
      >
        <ImageOff className="w-5 h-5 opacity-50" aria-hidden="true" />
        <span className="text-[10px] leading-none opacity-70 text-center px-1">Preview unavailable</span>
      </div>
    );
  }

  return (
    photoTileRenderingEnabled() ? <PhotoTile key={resolvedUrl} source={resolvedUrl} alt={alt} className={className} bucket={bucket} onClick={onClick} onSourceError={handleError} fill={fill} /> :
    <img
      ref={attachRef}
      src={resolvedUrl}
      alt={alt}
      loading={loading}
      decoding="async"
      data-loaded={loaded ? "true" : "false"}
      className={cn("bg-muted", className)}
      onClick={onClick}
      onLoad={() => setLoaded(true)}
      onError={handleError}
    />
  );
};

